<template>
  <div>
    <template v-if="tableHeaders.length && tableRows.length">
      <table
        v-for="(chunk, ci) in headerChunks"
        :key="ci"
        ref="tbls"
        class="result-table"
        :style="ci > 0 ? 'margin-top: 8px;' : ''"
      >
        <thead>
          <tr>
            <th>Channel</th>
            <th v-for="h in chunk" :key="h.key">{{ h.label }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in sortTableRows(tableRows)" :key="row.channelId">
            <td style="overflow: hidden;">
              <span
                v-if="row.color"
                class="colored-symbol"
                :style="{ backgroundColor: row.color }"
              />
              {{ row.channelLabel }}
              <template v-if="unitFor(row.channelId)">[{{ unitFor(row.channelId) }}]</template>
            </td>
            <td v-for="h in chunk" :key="h.key">
              {{ formatValue(row.values[h.key], decimalsFor(row.channelId)) }}
            </td>
          </tr>
        </tbody>
      </table>
    </template>
    <div v-else class="hint">No data</div>
  </div>
</template>

<script>
export default {
  name: "DecayResultsTable",
  emits: ["img-export"],
  props: {
    sltd_zone: { type: Object, required: true },
    tableHeaders: { type: Array, required: true },
    tableRows: { type: Array, required: true },
    automaticReport: { type: Boolean, default: false },
  },

  computed: {
    headerChunks() {
      const CHUNK = 12;
      const chunks = [];
      for (let i = 0; i < this.tableHeaders.length; i += CHUNK)
        chunks.push(this.tableHeaders.slice(i, i + CHUNK));
      return chunks;
    },
  },

  mounted() {
    // console.log("tableRows", this.tableRows)
    if (this.automaticReport) this.$nextTick(this.exportImg);
  },

  watch: {
    tableHeaders: { deep: true, handler() { if (this.automaticReport) this.$nextTick(this.exportImg); } },
    tableRows:    { deep: true, handler() { if (this.automaticReport) this.$nextTick(this.exportImg); } },
  },

  methods: {
    isOnVoltageChannel(id) {
			const node = this.nodeByChannelId(id);
			const ch = this.channelById(id);
			return !!(node?.is_power_supply && ch?.code === "U");
		},
    computeChannelOrder() {
			const order = new Map();
			if (!this.sltd_zone) return order;

			const channelSort = (a, b) =>
				(a?.io ?? 99) - (b?.io ?? 99) ||
				(a?.no ?? 9999) - (b?.no ?? 9999) ||
				(a?.id ?? 0) - (b?.id ?? 0);

			const nodes = this.$util?.sort_table
				? this.$util.sort_table(this.sltd_zone.nodes, "sortIndex")
				: [...(this.sltd_zone.nodes || [])];

			let rank = 0;
			for (const node of nodes) {
				const perNode = Object.values(node.channels || {}).sort(channelSort);
				for (const ch of perNode) {
					const id = Number(ch?.id) || ch?.id;
					if (!order.has(id)) order.set(id, rank++);
				}
			}
			return order;
		},
    sortTableRows(rows) {
			const order = this.computeChannelOrder();
			const INF = Number.POSITIVE_INFINITY;

			return [...rows].sort((a, b) => {
				const av = this.isOnVoltageChannel(a.channelId) ? 1 : 0;
				const bv = this.isOnVoltageChannel(b.channelId) ? 1 : 0;
				if (av !== bv) return av - bv;                // on-voltage to bottom
				const ra = order.has(a.channelId) ? order.get(a.channelId) : INF;
				const rb = order.has(b.channelId) ? order.get(b.channelId) : INF;
				if (ra !== rb) return ra - rb;                // project-tree order
				return a.channelId - b.channelId;             // stable fallback
			});
		},
    copyDotStyles(srcRoot, dstRoot) {
      const src = Array.from(srcRoot.querySelectorAll('.colored-symbol'));
      const dst = Array.from(dstRoot.querySelectorAll('.colored-symbol'));
      const n = Math.min(src.length, dst.length);
      for (let i = 0; i < n; i++) {
        const cs = getComputedStyle(src[i]);
        const bg = src[i].style.backgroundColor || cs.backgroundColor || '#000';
        const mr = cs.marginRight || '5px';
        Object.assign(dst[i].style, {
          display: 'inline-block',
          width: cs.width || '10px',
          height: cs.height || '10px',
          borderRadius: cs.borderRadius || '50%',
          backgroundColor: bg,
          marginRight: mr,
          verticalAlign: 'middle',
        });
      }
    },

    // Capture all table chunks and stitch them into one PNG image
    async exportImg() {
      const liveTables = (this.$refs.tbls
        ? (Array.isArray(this.$refs.tbls) ? this.$refs.tbls : [this.$refs.tbls])
        : []
      ).filter(Boolean);
      if (!liveTables.length) return null;
      const EXPORT_FONT_SIZE = "18px";
      const EXPORT_LINE_HEIGHT = "1.7";
      const holder = document.createElement("div");
      holder.style.cssText = "position:absolute; left:-99999px; top:0; visibility:hidden; background:#fff;";
      const tables = liveTables.map(src => {
        const w = Math.ceil(src.getBoundingClientRect().width);
        const c = src.cloneNode(true);
        c.style.width = w + "px";
        c.style.fontSize = EXPORT_FONT_SIZE;
        c.style.lineHeight = EXPORT_LINE_HEIGHT;
        c.querySelectorAll("th,td").forEach(cell => {
          cell.style.fontSize = EXPORT_FONT_SIZE;
          cell.style.lineHeight = EXPORT_LINE_HEIGHT;
        });
        holder.appendChild(c);
        return c;
      });
      document.body.appendChild(holder);

      try {
      void tables[0].offsetWidth;

      const GAP = 8; // px gap between chunks

      // Measure all tables
      const rects = tables.map(t => t.getBoundingClientRect());
      const W = Math.max(...rects.map(r => Math.ceil(r.width)));
      const BORDER_BUFFER = 6;
      const totalH = rects.reduce((s, r) => s + Math.ceil(r.height), 0)
                   + GAP * (tables.length - 1)
                   + BORDER_BUFFER;

      const dpr = Math.max(2, Math.ceil(window.devicePixelRatio || 1));
      const canvas = document.createElement("canvas");
      canvas.width  = W * dpr;
      canvas.height = totalH * dpr;
      const ctx = canvas.getContext("2d");
      ctx.imageSmoothingEnabled = false;
      ctx.scale(dpr, dpr);
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, W, totalH);

      let yOffset = 0;
      for (let i = 0; i < tables.length; i++) {
        const srcTbl = tables[i];
        const tW = Math.ceil(rects[i].width);
        const contentH = Math.ceil(rects[i].height);
        const tH = contentH + BORDER_BUFFER;

        const clone = srcTbl.cloneNode(true);
        this.inlineComputedStylesFrom(srcTbl, clone);
        this.lockTableLayout(clone, srcTbl);
        this.copyDotStyles(srcTbl, clone);
        clone.querySelectorAll("img").forEach(n => n.remove());
        clone.querySelectorAll("*").forEach(n => {
          const s = n.getAttribute("style") || "";
          if (s.includes("url(")) n.setAttribute("style", s.replace(/url\([^)]*\)/g, "none"));
        });
        clone.style.width = tW + "px";
        // don't force height — let it render naturally so the bottom border stays within tH

        const xhtml = new XMLSerializer().serializeToString(clone);
        const svgH = tH + tables.length * 5; // extra headroom so SVG viewport doesn't clip the bottom border
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${tW}" height="${svgH}">
          <foreignObject width="${tW}" height="${svgH}">
            <div xmlns="http://www.w3.org/1999/xhtml"
                 style="width:${tW}px;display:block;overflow:visible;background:#fff">
              ${xhtml}
            </div>
          </foreignObject>
        </svg>`;
        const svgDataUrl = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);

        const img = new Image();
        img.crossOrigin = "anonymous";
        await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = svgDataUrl; });

        ctx.drawImage(img, 0, 0, tW, tH, 0, yOffset, tW, tH); // source-clip first tH rows, no scaling
        yOffset += contentH + GAP; // advance by content height so gap stays exactly GAP px
      }

      const pngDataUrl = canvas.toDataURL("image/png");
      this.$emit("img-export", {
        url: pngDataUrl,
        width: W,
        height: totalH,
      });
      return pngDataUrl;
      } finally {
        holder.remove();
      }
    },

    // ------- Copy computed styles from the live table to the clone -------
    inlineComputedStylesFrom(srcRoot, dstRoot) {
      const wanted = [
        // table (height intentionally omitted — let SVG foreignObject render it naturally)
        "border-collapse","border-spacing","table-layout","width",
        // box
        "padding","margin","border","border-top","border-right","border-bottom","border-left",
        "border-color","border-style","border-width","box-sizing",
        // text
        "font","font-family","font-size","font-weight","font-style","line-height","letter-spacing",
        "color","text-align","vertical-align","white-space","word-break",
        // background
        "background","background-color","opacity"
      ];

      // Build parallel lists of nodes in tree order
      const srcWalker = document.createTreeWalker(srcRoot, NodeFilter.SHOW_ELEMENT, null);
      const dstWalker = document.createTreeWalker(dstRoot, NodeFilter.SHOW_ELEMENT, null);

      const srcNodes = [srcRoot]; while (srcWalker.nextNode()) srcNodes.push(srcWalker.currentNode);
      const dstNodes = [dstRoot]; while (dstWalker.nextNode()) dstNodes.push(dstWalker.currentNode);

      const n = Math.min(srcNodes.length, dstNodes.length);
      for (let i = 0; i < n; i++) {
        const src = srcNodes[i], dst = dstNodes[i];
        const cs = getComputedStyle(src);
        for (const p of wanted) {
          const v = cs.getPropertyValue(p);
          if (v != null && v !== "") dst.style.setProperty(p, v);
        }
      }

      // Force single-line cells to match UI intention
      dstRoot.querySelectorAll("th,td").forEach(el => {
        el.style.whiteSpace = "nowrap";
        el.style.wordBreak = "keep-all";
      });

      // Ensure borders/padding if defined via classes in app CSS
      dstRoot.style.borderCollapse = "collapse";
      dstRoot.querySelectorAll("th,td").forEach(td => {
        if (!td.style.border || td.style.border === "initial") td.style.border = "1px solid #e5e7eb";
        if (!td.style.padding || td.style.padding === "initial") td.style.padding = "4px 8px";
      });

      // Use common web-safe fonts to avoid substitution during rasterization
      dstRoot.style.fontFamily = 'Arial, Helvetica, sans-serif';
    },

    // ------- Lock widths from source to clone so columns stay identical -------
    lockTableLayout(dstRoot, srcRoot) {
      // table width
      const srcTableCS = getComputedStyle(srcRoot);
      dstRoot.style.tableLayout = srcTableCS.tableLayout || "auto";
      dstRoot.style.width = Math.ceil(srcRoot.getBoundingClientRect().width) + "px";

      // header widths
      const srcThs = Array.from(srcRoot.querySelectorAll("thead th"));
      const dstThs = Array.from(dstRoot.querySelectorAll("thead th"));
      srcThs.forEach((th, i) => {
        const w = Math.ceil(th.getBoundingClientRect().width);
        if (dstThs[i]) dstThs[i].style.width = w + "px";
      });

      // first row fallback for body cells
      const firstRowSrc = srcRoot.querySelector("tbody tr");
      const firstRowDst = dstRoot.querySelector("tbody tr");
      if (firstRowSrc && firstRowDst) {
        const sTds = Array.from(firstRowSrc.children);
        const dTds = Array.from(firstRowDst.children);
        sTds.forEach((td, i) => {
          const w = Math.ceil(td.getBoundingClientRect().width);
          if (dTds[i]) dTds[i].style.width = w + "px";
        });
      }
    },

    // ------- Value formatting and metadata -------
    formatValue(v, decimals = 3) {
      if (v == null) return "-";
      if (Number.isFinite(Number(v))) {
        const n = Number(decimals);
        const d = Number.isInteger(n) && n >= 0 ? n : 3;
        return Number(v).toFixed(d);
      }
      return v;
    },

    nodeByChannelId(id) {
			const zone = this.sltd_zone;
			for (const node of zone.nodes) {
				for (const key in node.channels) {
					const ch = node.channels[key];
					if (ch.id === id) return node;
				}
			}
			return null;
		},

    channelById(id) {
      const zone = this.sltd_zone;
      for (const node of zone.nodes) {
        for (const key in node.channels) {
          const ch = node.channels[key];
          if (ch.id === id) return ch;
        }
      }
      return null;
    },

    unitFor(id) {
      const ch = this.channelById(id);
      return ch && ch.formula && ch.formula.unit ? ch.formula.unit : null;
    },

    decimalsFor(id) {
      const ch = this.channelById(id);
      const dec = ch && ch.formula && Number.isFinite(Number(ch.formula.decimals))
        ? Number(ch.formula.decimals)
        : null;
      return dec != null ? dec : 3;
    },
  },
};
</script>

<style scoped>
.result-table,
.result-table th,
.result-table td {
  font-family: "Arial Nova", Arial, Helvetica, sans-serif;
  font-size: 12px;
  line-height: 1.5;
}

.result-table {
  border-collapse: collapse;
  width: 100%;
  background: #fff;
}

.result-table th,
.result-table td {
  border: 1px solid #e5e7eb;
  padding: 4px 8px;
  text-align: left;
  vertical-align: middle;
  white-space: nowrap;      /* single-line cells in UI */
  word-break: keep-all;     /* avoid breaking long tokens */
}

.hint { margin-top: 6px; color: #666; }
.colored-symbol {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 5px;
}
</style>
